/* tslint:disable:max-line-length */

import {HttpClient, HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import { APIConfigService } from '../apiconfig.service';

import * as __utils from '../yasag-utils';

import * as __model from '../model';

export interface ListParams {
  /** A search term. */
  search?: string;
  /** Number of results to return per page. */
  limit?: number;
  /** The initial index from which to return the results. */
  offset?: number;
  /** Categoria */
  id?: string;
  /** Lang */
  lang?: LangListParamsEnum;
  /** List of fields */
  fields?: string;
  /** List of nested objects */
  expand?: string;
}

export type LangListParamsEnum =
  'ca' |
  'es' |
  'en';

export interface CreateParams {
  data: __model.Categoria;
  /** List of fields */
  fields?: string;
  /** List of nested objects */
  expand?: string;
}

export interface ExportCategoriesParams {
  /** A search term. */
  search?: string;
  /** Number of results to return per page. */
  limit?: number;
  /** The initial index from which to return the results. */
  offset?: number;
  /** List of fields */
  fields?: string;
  /** List of nested objects */
  expand?: string;
}

export interface ImportCategoriesParams {
  data: __model.ImportCSV;
  /** List of fields */
  fields?: string;
  /** List of nested objects */
  expand?: string;
}

export interface ReadParams {
  /** List of fields */
  fields?: string;
  /** List of nested objects */
  expand?: string;
  /** A unique integer value identifying this categoria. */
  id: number;
}

export interface UpdateParams {
  data: __model.Categoria;
  /** List of fields */
  fields?: string;
  /** List of nested objects */
  expand?: string;
  /** A unique integer value identifying this categoria. */
  id: number;
}

export interface PartialUpdateParams {
  data: __model.Categoria;
  /** List of fields */
  fields?: string;
  /** List of nested objects */
  expand?: string;
  /** A unique integer value identifying this categoria. */
  id: number;
}

export interface DeleteParams {
  /** A unique integer value identifying this categoria. */
  id: number;
}

@Injectable()
export class CategoriaService {
  constructor(
    private http: HttpClient,
    private apiConfigService: APIConfigService) {}


  list(params: ListParams, multipart = false): Observable<__model.CategoriaList> {
    const queryParamBase = {
      search: params.search,
      limit: params.limit,
      offset: params.offset,
      id: params.id,
      lang: params.lang,
      fields: params.fields,
      expand: params.expand,
    };

    let queryParams = __utils.getQueryParams(queryParamBase);

    return this.http.get<__model.CategoriaList>(this.apiConfigService.options.apiUrl + `/categoria/`, {params: queryParams});
  }

  /** Creates an attribute category */
  create(params: CreateParams, multipart = false): Observable<__model.Categoria> {
    const bodyParams = params.data;
    const bodyParamsWithoutUndefined = __utils.getBodyParamsWithoutUndefined(multipart, bodyParams);

    const queryParamBase = {
      fields: params.fields,
      expand: params.expand,
    };

    let queryParams = __utils.getQueryParams(queryParamBase);

    return this.http.post<__model.Categoria>(this.apiConfigService.options.apiUrl + `/categoria/`, bodyParamsWithoutUndefined, {params: queryParams});
  }

  /** Endpoint to export categories to CSV */
  exportCategories(params: ExportCategoriesParams, multipart = false): Observable<string> {
    const queryParamBase = {
      search: params.search,
      limit: params.limit,
      offset: params.offset,
      fields: params.fields,
      expand: params.expand,
    };

    let queryParams = __utils.getQueryParams(queryParamBase);

    return this.http.get(this.apiConfigService.options.apiUrl + `/categoria/export_categories/`, {params: queryParams, responseType: 'text'});
  }

  /** Endpoint to import categories */
  importCategories(params: ImportCategoriesParams, multipart = false): Observable<string> {
    const bodyParams = params.data;
    const bodyParamsWithoutUndefined = __utils.getBodyParamsWithoutUndefined(multipart, bodyParams);

    const queryParamBase = {
      fields: params.fields,
      expand: params.expand,
    };

    let queryParams = __utils.getQueryParams(queryParamBase);

    return this.http.post(this.apiConfigService.options.apiUrl + `/categoria/import_categories/`, bodyParamsWithoutUndefined, {params: queryParams, responseType: 'text'});
  }

  /** Gets an attribute category with its attributes */
  read(params: ReadParams, multipart = false): Observable<__model.Categoria> {
    const queryParamBase = {
      fields: params.fields,
      expand: params.expand,
    };

    let queryParams = __utils.getQueryParams(queryParamBase);

    const pathParams = {
      id: params.id,
    };
    return this.http.get<__model.Categoria>(this.apiConfigService.options.apiUrl + `/categoria/${pathParams.id}/`, {params: queryParams});
  }

  /** Updates an attribute category */
  update(params: UpdateParams, multipart = false): Observable<__model.Categoria> {
    const bodyParams = params.data;
    const bodyParamsWithoutUndefined = __utils.getBodyParamsWithoutUndefined(multipart, bodyParams);

    const queryParamBase = {
      fields: params.fields,
      expand: params.expand,
    };

    let queryParams = __utils.getQueryParams(queryParamBase);

    const pathParams = {
      id: params.id,
    };
    return this.http.put<__model.Categoria>(this.apiConfigService.options.apiUrl + `/categoria/${pathParams.id}/`, bodyParamsWithoutUndefined, {params: queryParams});
  }

  /** Updates an attribute category partially */
  partialUpdate(params: PartialUpdateParams, multipart = false): Observable<__model.Categoria> {
    const bodyParams = params.data;
    const bodyParamsWithoutUndefined = __utils.getBodyParamsWithoutUndefined(multipart, bodyParams);

    const queryParamBase = {
      fields: params.fields,
      expand: params.expand,
    };

    let queryParams = __utils.getQueryParams(queryParamBase);

    const pathParams = {
      id: params.id,
    };
    return this.http.patch<__model.Categoria>(this.apiConfigService.options.apiUrl + `/categoria/${pathParams.id}/`, bodyParamsWithoutUndefined, {params: queryParams});
  }

  /** Deletes an attribute category from the DB */
  delete(params: DeleteParams, multipart = false): Observable<string> {
    const pathParams = {
      id: params.id,
    };
    return this.http.delete(this.apiConfigService.options.apiUrl + `/categoria/${pathParams.id}/`, {responseType: 'text'});
  }
}
